import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Footer } from '../brokerPortalV2/common_v2/components/Footer';
import { alertModal } from '@apply/ui';
import { ChangePasswordResponse } from '@apply/store/src/cross';
import { isMobileScreen } from '@apply/ui';
import { Panel, PanelTheme, PanelBorderRadius, TextBox, Button, ButtonTheme } from '@apply/components';
import { LoginApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import store, { snackbarActions } from '@apply/adminStore';

interface PasswordResetProps {
    createPassword?: boolean;
}

export const PasswordReset: React.FC<PasswordResetProps> = ({ createPassword }) => {
    const [statusCheckCompleted, set_statusCheckCompleted] = useState(false);
    const loginApi = useMemo(() => new LoginApi.LoginApi(applicationInsights), []);
    const navigate = useNavigate();
    const mobileScreen = isMobileScreen();
    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const id = params.get('id');
    const code = params.get('code');
    const emailParam = params.get('email');

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [matchingError, setMatchingError] = useState<boolean>(false);
    const [passwordResetResult, setPasswordResetResult] = useState<ChangePasswordResponse>(null);
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    const isCreatePassword = !!createPassword;
    const headerText = isCreatePassword ? 'Create Password' : 'Reset Password';

    const ChangePasswordValidationMessage = {
        passwordNotMatching: 'Passwords do not match',
        passwordTooShort: 'At least 8 characters',
        passwordRequiresMixedCase: 'A mixture of both uppercase and lowercase letters',
        passwordRequiresDigit: 'A mixture of letters and numbers',
        passwordRequiresSpecialCharacter: 'Inclusion of at least one special character, e.g., ! @ # ?',
    };

    useEffect(() => {
        const runStatusCheck = async () => {
            const response = await loginApi.getUserStatus({ userId: id });

            if (response.statusCode !== 200) {
                set_statusCheckCompleted(true);
                return;
            }

            const { result } = response;

            if (!result.isUserDeleted && !result.needsPasswordReset) {
                navigate('/');
                return; // do NOT mark completed if redirecting
            }

            set_statusCheckCompleted(true);
        };

        set_statusCheckCompleted(false);
        runStatusCheck();
    }, [id, navigate]);

    useEffect(() => {
        const runPasswordPreCheck = async () => {
            const response = await loginApi.resetPasswordPreCheck({ userId: id, resetToken: code });
            if (response.statusCode !== 200) {
                const { result } = response;
                if (result?.errors?.length > 0) {
                    alertModal({ message: result.errors.join('; ') });
                    navigate('/');
                }
            }
        };
        runPasswordPreCheck();
    }, [id, code]);

    useEffect(() => {
        setMatchingError(false);
        setPasswordResetResult(null);

        if (!password || !passwordConfirm) {
            return;
        }

        if (password && passwordConfirm && password !== passwordConfirm) {
            setMatchingError(true);
            return;
        }
    }, [password, passwordConfirm]);

    useEffect(() => {
        if (submitError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
            });
            setSubmitError(undefined);
        }
    }, [submitError]);

    const readyToSubmit = () => {
        return password.length > 0 && passwordConfirm.length > 0 && password === passwordConfirm;
    };

    const handlePasswordReset = async () => {
        if (!readyToSubmit()) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await loginApi.resetPassword({ userId: id, password, resetToken: code });

            setSubmitting(false);

            // Success
            if (response.statusCode === 200) {
                const result = response.result;
                if (result.result.succeeded) {
                    navigate(`/login${emailParam ? `?email=${emailParam}` : ''}`);
                    return;
                } else {
                    if (result.errors && result.errors.length > 0) {
                        throw new Error(result.errors);
                    } else {
                        //Some kind of password policy failure occurred
                        setPasswordResetResult(result);
                    }
                }
            } else {
                throw new Error('No response.result.result returned');
            }
        } catch (ex) {
            store.dispatch(
                snackbarActions.updateSnackbar({
                    isOpen: true,
                    message: ex.message,
                    variant: 'error',
                })
            );
        }
    };

    const onEnterKey = () => {
        // only submit if its valid
        if (readyToSubmit()) {
            handlePasswordReset();
        }
    };

    const renderChangePasswordContent = () => {
        return (
            <>
                <main>
                    <h2>{headerText}</h2>
                    <div className='changePassword subheader'>
                        Enter your new password. It must meet the following criteria:
                        <br />
                        <br />
                        <ul>
                            <li>{ChangePasswordValidationMessage.passwordTooShort}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresMixedCase}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresDigit}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresSpecialCharacter}</li>
                        </ul>
                    </div>
                    <TextBox
                        id='password'
                        dataTestid='resetpassword-password'
                        type='password'
                        value={password}
                        onChange={setPassword}
                        onEnterKey={onEnterKey}
                        label='Password'
                        name='password'
                        required={true}
                    />
                    <TextBox
                        id='confirmPassword'
                        dataTestid='resetpassword-confirm-password'
                        type='password'
                        value={passwordConfirm}
                        onChange={setPasswordConfirm}
                        onEnterKey={onEnterKey}
                        label='Confirm Password'
                        name='confirmPassword'
                        required={true}
                    />
                    {(matchingError || (passwordResetResult && passwordResetResult.result)) && (
                        <div className='changePassword validationError'>
                            Your password failed to meet the requirements indicated below:
                            <ul>
                                {matchingError && <li>{ChangePasswordValidationMessage.passwordNotMatching}</li>}
                                {passwordResetResult?.result.passwordTooShort && (
                                    <li>{ChangePasswordValidationMessage.passwordTooShort}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresMixedCase && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresMixedCase}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresDigit && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresDigit}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresSpecialCharacter && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresSpecialCharacter}</li>
                                )}
                            </ul>
                        </div>
                    )}
                    <div className='changePassword primaryActions'>
                        <Button
                            id='resetpassword-button'
                            dataTestid='resetpassword-button'
                            key='resetpassword-button'
                            width='80%'
                            theme={ButtonTheme.Primary}
                            disabled={submitting || !readyToSubmit()}
                            handleClick={handlePasswordReset}
                        >
                            {createPassword ? 'Set Password' : 'Reset Password'}
                        </Button>
                    </div>
                    <div className='changePassword secondaryActions'>
                        <div style={{ width: '100%', float: 'left' }}>
                            <Button
                                id='login-button'
                                dataTestid='navigate-login-button'
                                key='login-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={() => navigate('/login')}
                                width='100px'
                            >
                                Log In
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    if (!statusCheckCompleted) {
        return null;
    }

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>
                                    {renderChangePasswordContent()}
                                </div>
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderChangePasswordContent()}</div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};
